import * as XLSX from 'xlsx';

const COLUMNS = [
  'Service No *', 'Category (Loan/Other) *', 'Loan Type *', 
  'Start Period Code *', 'First Due Date (YYYY-MM-DD) *',
  'Loan Amount *', 'Annual Interest Rate % *', 'Installments *', 'Description'
];

export const downloadLoanTemplate = () => {
  const wb = XLSX.utils.book_new();
  const sampleData = [{
    'Service No *': 'EMP001',
    'Category (Loan/Other) *': 'Loan',
    'Loan Type *': 'Personal',
    'Start Period Code *': 'PR2026-01',
    'First Due Date (YYYY-MM-DD) *': '2026-01-25',
    'Loan Amount *': 100000,
    'Annual Interest Rate % *': 12,
    'Installments *': 12,
    'Description': 'Bulk import loan'
  }];
  
  const ws = XLSX.utils.json_to_sheet(sampleData, { header: COLUMNS });
  ws['!cols'] = COLUMNS.map(() => ({ wch: 22 }));
  XLSX.utils.book_append_sheet(wb, ws, 'Loans');
  XLSX.writeFile(wb, 'Loan_Import_Template.xlsx');
};

export const readLoanWorkbook = async (file) => {
  const buffer = await file.arrayBuffer();
  const wb = XLSX.read(buffer, { type: 'array' });
  const ws = wb.Sheets[wb.SheetNames[0]];
  if (!ws) return [];

  return XLSX.utils.sheet_to_json(ws, { defval: '' }).map((row, idx) => ({
    _row: idx + 2,
    serviceNo: String(row['Service No *'] ?? '').trim(),
    categoryStr: String(row['Category (Loan/Other) *'] ?? '').trim(),
    loanTypeName: String(row['Loan Type *'] ?? '').trim(),
    periodCode: String(row['Start Period Code *'] ?? '').trim(),
    dueDate: String(row['First Due Date (YYYY-MM-DD) *'] ?? '').trim(),
    amountStr: String(row['Loan Amount *'] ?? '').trim(),
    rateStr: String(row['Annual Interest Rate % *'] ?? '').trim(),
    installmentsStr: String(row['Installments *'] ?? '').trim(),
    description: String(row['Description'] ?? '').trim(),
  }));
};

export const validateLoanRow = (row, employees, loanTypes, periods) => {
  const errors = [];
  let employeeId = '', loanCategory = 'M', loanTypeId = '', startPeriodId = '';
  let loanAmount = 0, interestRate = 0, noOfInstallments = 0;

  // 1. Validate Employee
  if (!row.serviceNo) errors.push('Service No is required');
  else {
    const emp = employees.find(e => String(e.HED_SERVICE_NO).toLowerCase() === row.serviceNo.toLowerCase());
    if (emp) employeeId = emp.HED_EMPLOYEE_ID;
    else errors.push(`Employee ${row.serviceNo} not found`);
  }

  // 2. Validate Category & Type
  if (row.categoryStr.toLowerCase().includes('other')) loanCategory = 'O';
  const categoryName = loanCategory === 'O' ? 'Other Loans' : 'Loans';
  
  if (!row.loanTypeName) errors.push('Loan Type is required');
  else {
    const type = loanTypes.find(t => 
      t.typeName.toLowerCase() === row.loanTypeName.toLowerCase() && 
      t.categoryCode === loanCategory
    );
    if (type) {
      if (!type.isActive) errors.push(`Loan Type '${row.loanTypeName}' is inactive`)
      else loanTypeId = type.id
    } else {
      errors.push(`Loan Type '${row.loanTypeName}' not found in category '${categoryName}'`)
    }
  }

  // 3. Validate Payroll Period
  if (!row.periodCode) errors.push('Start Period Code is required');
  else {
    const period = periods.find(p => p.code.toLowerCase() === row.periodCode.toLowerCase());
    
    if (!period) {
        errors.push(`Period Code ${row.periodCode} not found`);
    } else if (period.status !== 'Active') {
        errors.push(`Payroll Period ${row.periodCode} is inactive`);
    } else {
        startPeriodId = period.id;
    }
  }

  // 4. Validate Due Date
  if (!row.dueDate) errors.push('Due Date is required');
  else if (isNaN(Date.parse(row.dueDate))) errors.push('Due Date format invalid (Use YYYY-MM-DD)');

  // 5. Validate Numbers
  loanAmount = Number(row.amountStr.replace(/,/g, ''));
  if (isNaN(loanAmount) || loanAmount <= 0) errors.push('Invalid Loan Amount');

  const parsedRate = Number(row.rateStr);
  if (isNaN(parsedRate) || parsedRate < 0) {
    errors.push("Invalid interest rate")
  } else if (loanCategory === 'O' && parsedRate !== 0) {
    errors.push("Interest rate must be 0 for Other Loans");
  } else {
    interestRate = parsedRate;
  }

  noOfInstallments = parseInt(row.installmentsStr, 10);
  if (isNaN(noOfInstallments) || noOfInstallments <= 0) errors.push('Invalid Installments count');

  // 6. EMI Math
  let installmentAmount = 0, totalAmount = 0, totalInterest = 0;
  
  // Calculate EMI as long as the numbers are valid, despite there being other errors
  if (!isNaN(loanAmount) && loanAmount > 0 && !isNaN(noOfInstallments) && noOfInstallments > 0 && !isNaN(interestRate)) {
    const monthlyRate = interestRate / 12 / 100;
    if (monthlyRate === 0) {
      installmentAmount = Math.round((loanAmount / noOfInstallments) * 100) / 100;
    } else {
      const factor = Math.pow(1 + monthlyRate, noOfInstallments);
      const emi = (loanAmount * monthlyRate * factor) / (factor - 1);
      installmentAmount = Math.round(emi * 100) / 100;
    }
    totalAmount = Math.round(installmentAmount * noOfInstallments * 100) / 100;
    totalInterest = Math.max(0, Math.round((totalAmount - loanAmount) * 100) / 100);
  }

  return {
    values: {
      employeeId, loanCategory, loanTypeId, loanType: row.loanTypeName,
      startPeriodId, dueDate: row.dueDate, loanDescription: row.description,
      loanAmount, interestRate, noOfInstallments, installmentAmount, 
      totalAmount, totalInterest, balanceAmount: loanAmount,
      status: loanCategory === 'O' ? 'P' : 'P',
      isActive: loanCategory === 'O'
    },
    errors
  };
};